/**
 * Live action trace: the steps Partnr takes while it works (searches, folders,
 * links), streamed by partnr-chat as `partnr_step` SSE events.
 */

export type PartnrTraceStatus = 'running' | 'done' | 'error';

export interface PartnrTraceStep {
  id: string;
  status: PartnrTraceStatus;
  tool: string;
  label: string;
}

/** Saved on the assistant message so the chip survives a reload. */
export interface PartnrTrace {
  steps: PartnrTraceStep[];
  /** Seconds from sending to the first word of the answer. */
  seconds: number;
}

const STATUSES: PartnrTraceStatus[] = ['running', 'done', 'error'];

/** Validate a `partnr_step` payload from the stream. */
export function parseTraceStep(value: unknown): PartnrTraceStep | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  if (typeof v.id !== 'string' || typeof v.label !== 'string') return null;
  if (!STATUSES.includes(v.status as PartnrTraceStatus)) return null;
  return {
    id: v.id,
    status: v.status as PartnrTraceStatus,
    tool: typeof v.tool === 'string' ? v.tool : '',
    label: v.label.substring(0, 200),
  };
}

/** Add a step, or update it in place when its id is already there. */
export function upsertTraceStep(steps: PartnrTraceStep[], step: PartnrTraceStep): PartnrTraceStep[] {
  const index = steps.findIndex((s) => s.id === step.id);
  if (index === -1) return [...steps, step];
  return steps.map((s, i) => (i === index ? step : s));
}

/** "Did 4 things · 6s" */
export function traceSummary(trace: PartnrTrace): string {
  const n = trace.steps.length;
  return `Did ${n} thing${n === 1 ? '' : 's'} · ${Math.max(1, trace.seconds)}s`;
}
